import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Tabs, Tab, Card, Grid, CircularProgress, Chip, Icon } from "@mui/material";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import "bootstrap/dist/css/bootstrap.min.css";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_ENDPOINTS } from "config/api";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";


// Custom status badge renderer for Cleaning Status
const renderStatusBadge = (status) => {
  const config = {
    "To do": { label: "🔴 Task: To do", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
    "In progress": { label: "🟢 Task: In progress", color: "#2563eb", bg: "#eff6ff", border: "#93c5fd" },
    "Hold": { label: "⏸ Task: Hold", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    "Clean": { label: "✅ Task: Clean", color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
  }[status] || { label: "🔴 Task: To do", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" };

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1.5px solid ${config.border}`,
        fontWeight: "bold",
        fontSize: "0.7rem",
        borderRadius: "8px",
        px: 1,
      }}
    />
  );
};

// Custom status badge renderer for compact room cards
const renderStatusBadgeMini = (status) => {
  const config = {
    "To do": { label: "🔴 To do", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
    "In progress": { label: "🟢 In progress", color: "#2563eb", bg: "#eff6ff", border: "#93c5fd" },
    "Hold": { label: "⏸ Hold", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    "Clean": { label: "✅ Clean", color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
  }[status] || { label: "🔴 To do", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" };

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1.2px solid ${config.border}`,
        fontWeight: "bold",
        fontSize: "0.58rem",
        borderRadius: "6px",
        height: "18px",
        px: 0.5,
        "& .MuiChip-label": {
          px: 0.5,
        }
      }}
    />
  );
};

// Compact date/time formatter to fit within card columns
const formatTimeCompact = (dateTimeStr) => {
  if (!dateTimeStr || dateTimeStr.trim() === "" || dateTimeStr.toLowerCase() === "n/a") return "N/A";
  try {
    const clean = dateTimeStr.trim();
    if (clean.includes(",")) {
      const parts = clean.split(",");
      const datePart = parts[0].trim(); // "06/17/2026"
      const timePart = parts[1].trim(); // "12:39:59 PM"

      const dateSubparts = datePart.split("/");
      const monthDay = dateSubparts.length === 3 ? `${dateSubparts[0]}/${dateSubparts[1]}` : datePart;

      const timeSubparts = timePart.split(" ");
      const hms = timeSubparts[0].split(":");
      const ampm = timeSubparts[1] || "";
      const hm = hms.length >= 2 ? `${hms[0]}:${hms[1]}` : timePart;

      return `${monthDay} ${hm} ${ampm}`.trim();
    }
    return clean;
  } catch (e) {
    return dateTimeStr;
  }
};

// Format ISO timestamp to show date and time portion (Pakistan timezone)
const formatFullDateTime = (isoStr) => {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return String(isoStr);
    return d.toLocaleString("en-US", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return String(isoStr);
  }
};

// Dynamic color getter for Status values
const getStatusColor = (statusStr) => {
  const status = String(statusStr || "").trim().toLowerCase();
  if (status.includes("staying") || status.includes("checked in") || status.includes("checkin")) {
    return "#2563eb"; // Blue
  }
  if (status.includes("checkout") || status.includes("check out")) {
    if (status.includes("upcoming")) {
      return "#d97706"; // Amber
    }
    return "#16a34a"; // Green
  }
  if (status.includes("upcoming") || status.includes("reserved")) {
    return "#d97706"; // Amber
  }
  return "#64748b"; // Grey
};

// Get today's date string in Pakistan timezone (YYYY-MM-DD)
const getPakistanDateString = (dateObj = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
    return formatter.format(dateObj); // returns "YYYY-MM-DD"
  } catch (err) {
    return "";
  }
};

// Parse FDO date/time string to YYYY-MM-DD
const parseFdoDateToYYYYMMDD = (fdoDateStr) => {
  if (!fdoDateStr || typeof fdoDateStr !== 'string') return '';
  const clean = fdoDateStr.trim();
  if (clean.toLowerCase() === 'n/a' || clean === '') return '';

  if (clean.includes('/')) {
    const parts = clean.split(',')[0].trim().split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
  } else if (clean.includes('-')) {
    return clean.split('T')[0];
  }
  return '';
};

// Helper to clean trailing Reservation ID from room names
const getBaseName = (fullName) => {
  if (!fullName) return "";
  const str = String(fullName).trim();
  const match = str.match(/\s+\((\d+)\)$/);
  if (match) {
    return str.substring(0, str.length - match[0].length).trim();
  }
  return str;
};

// Merge reservations from FDO and Cleaning
const mergeReservations = (fdoMatches, cleaningMatches) => {
  const mergedMap = {};

  fdoMatches.forEach(r => {
    const f = r.fields || {};
    const resId = String(f["Reservation ID"] || f["Reservation ID "] || "").trim();
    if (!resId) return;

    const arrival = f["Arrival Date"] || f["Arrival Date "] || "";
    const departure = f["Departure Date"] || f["Departure Date "] || "";
    const checkin = f["Actual Checkin"] || "";
    const checkout = f["Actual Checkout"] || "";
    const statusVal = f["Status"] || f["Status "] || "";

    const checkinEmpty = !checkin || checkin.trim() === "" || checkin.trim().toLowerCase() === "n/a";
    const checkoutEmpty = !checkout || checkout.trim() === "" || checkout.trim().toLowerCase() === "n/a";

    mergedMap[resId] = {
      reservationId: resId,
      arrivalDate: arrival ? String(arrival).trim() : "",
      departureDate: departure ? String(departure).trim() : "",
      actualCheckin: checkin ? String(checkin).trim() : "",
      actualCheckout: checkout ? String(checkout).trim() : "",
      column: "To do",
      createdBy: "",
      apartmentStatus: statusVal ? String(statusVal).trim() : ((checkinEmpty || !checkoutEmpty) ? "Available" : "Reserved")
    };
  });

  cleaningMatches.forEach(c => {
    const resId = String(c.reservationId || "").trim();
    if (!resId) return;

    if (mergedMap[resId]) {
      mergedMap[resId].column = c.column || "To do";
      mergedMap[resId].createdBy = c.createdBy || "";
      if (c.actualCheckin) mergedMap[resId].actualCheckin = c.actualCheckin;
      if (c.actualCheckout) mergedMap[resId].actualCheckout = c.actualCheckout;
      if (c.arrivalDate) mergedMap[resId].arrivalDate = c.arrivalDate;
      if (c.departureDate) mergedMap[resId].departureDate = c.departureDate;
      if (c.apartmentStatus && !mergedMap[resId].apartmentStatus) {
        mergedMap[resId].apartmentStatus = c.apartmentStatus;
      }
    } else {
      const checkinEmpty = !c.actualCheckin || c.actualCheckin.trim() === "" || c.actualCheckin.trim().toLowerCase() === "n/a";
      const checkoutEmpty = !c.actualCheckout || c.actualCheckout.trim() === "" || c.actualCheckout.trim().toLowerCase() === "n/a";
      mergedMap[resId] = {
        reservationId: resId,
        arrivalDate: c.arrivalDate || "",
        departureDate: c.departureDate || "",
        actualCheckin: c.actualCheckin || "",
        actualCheckout: c.actualCheckout || "",
        column: c.column || "To do",
        createdBy: c.createdBy || "",
        apartmentStatus: c.apartmentStatus || ((checkinEmpty || !checkoutEmpty) ? "Available" : "Reserved")
      };
    }
  });

  return Object.values(mergedMap).filter(r => {
    const hasArrival = r.arrivalDate && r.arrivalDate.trim() !== "" && r.arrivalDate.toLowerCase() !== "n/a";
    const hasDeparture = r.departureDate && r.departureDate.trim() !== "" && r.departureDate.toLowerCase() !== "n/a";
    const hasCheckin = r.actualCheckin && r.actualCheckin.trim() !== "" && r.actualCheckin.toLowerCase() !== "n/a";
    const hasCheckout = r.actualCheckout && r.actualCheckout.trim() !== "" && r.actualCheckout.toLowerCase() !== "n/a";
    return hasArrival || hasDeparture || hasCheckin || hasCheckout;
  });
};

// Filter double reservations on the same day for a room helper
const filterDoubleReservations = (list) => {
  if (!list || list.length === 0) return [];

  const parseDate = (d) => {
    if (!d) return "";
    const clean = String(d).trim();
    if (clean.toLowerCase() === "n/a" || clean === "") return "";
    if (clean.includes("/")) {
      const parts = clean.split(",")[0].trim().split("/");
      if (parts.length === 3) {
        const month = parts[0].padStart(2, "0");
        const day = parts[1].padStart(2, "0");
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    } else if (clean.includes("-")) {
      return clean.split("T")[0];
    }
    return clean;
  };

  const normRoom = (name) => {
    if (!name) return "";
    return String(name).replace(/\s+/g, "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  };

  const getProp = (r, propName) => {
    if (!r) return "";
    const f = r.fields || {};
    if (propName === "room") {
      return r.listingName || r.apartment || f["Listing Name"] || f["Apartment Name"] || f["Apartment Name "] || "";
    }
    if (propName === "arrival") {
      return r.arrivalDate || r.arrival || f["Arrival Date"] || f["Arrival Date "] || "";
    }
    if (propName === "departure") {
      return r.departureDate || r.departure || f["Departure Date"] || f["Departure Date "] || "";
    }
    if (propName === "checkin") {
      return r.actualCheckin || f["Actual Checkin"] || "";
    }
    if (propName === "checkout") {
      return r.actualCheckout || f["Actual Checkout"] || "";
    }
    if (propName === "id") {
      return r.reservationId || r.id || f["Reservation ID"] || f["Reservation ID "] || "";
    }
    return "";
  };

  // Group by room and stay dates
  const groups = {};
  list.forEach((r) => {
    const room = normRoom(getProp(r, "room"));
    const arrival = parseDate(getProp(r, "arrival"));
    const departure = parseDate(getProp(r, "departure"));
    const key = `${room}_${arrival}_${departure}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });

  const toRemove = new Set();

  Object.values(groups).forEach((group) => {
    if (group.length <= 1) return;

    // Check if there is one with check-in populated and checkout NOT populated
    const activeGuest = group.find((r) => {
      const checkinVal = getProp(r, "checkin");
      const checkoutVal = getProp(r, "checkout");
      const checkin = checkinVal && checkinVal !== "N/A" && String(checkinVal).trim() !== "";
      const checkout = checkoutVal && checkoutVal !== "N/A" && String(checkoutVal).trim() !== "";
      return checkin && !checkout;
    });

    if (activeGuest) {
      const activeId = getProp(activeGuest, "id");
      group.forEach((r) => {
        if (getProp(r, "id") !== activeId) {
          toRemove.add(getProp(r, "id"));
        }
      });
      return;
    }

    // Otherwise, check if there is one with both check-in and checkout populated
    const completedGuest = group.find((r) => {
      const checkinVal = getProp(r, "checkin");
      const checkoutVal = getProp(r, "checkout");
      const checkin = checkinVal && checkinVal !== "N/A" && String(checkinVal).trim() !== "";
      const checkout = checkoutVal && checkoutVal !== "N/A" && String(checkoutVal).trim() !== "";
      return checkin && checkout;
    });

    if (completedGuest) {
      const completedId = getProp(completedGuest, "id");
      group.forEach((r) => {
        if (getProp(r, "id") !== completedId) {
          toRemove.add(getProp(r, "id"));
        }
      });
    }
  });

  return list.filter((r) => !toRemove.has(getProp(r, "id")));
};

// Helpers for check-in / check-out and reservation status checks
const hasActualCheckin = (res) => {
  if (!res) return false;
  const v = String(res.actualCheckin || '').trim();
  return v !== '' && v.toLowerCase() !== 'n/a';
};

const hasActualCheckout = (res) => {
  if (!res) return false;
  const v = String(res.actualCheckout || '').trim();
  return v !== '' && v.toLowerCase() !== 'n/a';
};

const isCheckedOutOrCancelled = (res) => {
  if (!res) return false;
  const status = String(res.apartmentStatus || '').trim().toLowerCase();
  // "Upcoming Checkout" means guest is STILL in the room — never treat as checked out
  if (status === 'upcoming checkout') return false;
  return status.includes('check out') ||
    status.includes('checkout') ||
    status.includes('checked out') ||
    status.includes('checkedout') ||
    status.includes('cancelled') ||
    status.includes('available') ||
    status === 'unknown' ||
    status === '';
};

// Check for Upcoming Checkout:
// 1. FDO status is directly "Upcoming Checkout", OR
// 2. Departure == today + arrival != today + actual check-in populated + actual checkout NOT populated
const isUpcomingCheckout = (res, todayStr = getPakistanDateString()) => {
  if (!res) return false;
  const status = String(res.apartmentStatus || '').trim().toLowerCase();
  // Direct FDO status match
  if (status === 'upcoming checkout') return true;
  // Condition-based detection
  const depDate = parseFdoDateToYYYYMMDD(res.departureDate);
  const arrDate = parseFdoDateToYYYYMMDD(res.arrivalDate);
  return depDate === todayStr && arrDate !== todayStr && hasActualCheckin(res) && !hasActualCheckout(res);
};

// Classify reservations into Yesterday and Today
const classifyReservations = (mergedResList) => {
  const todayStr = getPakistanDateString();
  const yesterdayStr = getPakistanDateString(new Date(Date.now() - 86400000));

  // Helper to check if a reservation is cancelled
  const isCancelled = (r) => {
    const status = String(r.apartmentStatus || '').trim().toLowerCase();
    return status.includes('cancelled');
  };

  // Helper to get effective departure date (if checked out early, use checkout date)
  const getEffectiveDepartureDate = (r) => {
    const dep = parseFdoDateToYYYYMMDD(r.departureDate);
    const checkout = parseFdoDateToYYYYMMDD(r.actualCheckout);
    if (checkout && checkout < dep) {
      return checkout;
    }
    return dep;
  };

  // 1. Identify yesterday's candidates (reservations that cover yesterdayStr)
  const yesterdayCandidates = mergedResList.filter(r => {
    if (isCancelled(r)) return false;
    const arr = parseFdoDateToYYYYMMDD(r.arrivalDate);
    const dep = getEffectiveDepartureDate(r);
    return arr && dep && yesterdayStr >= arr && yesterdayStr <= dep;
  });

  // Sort yesterday candidates: prefer departures closest to today (descending)
  if (yesterdayCandidates.length > 0) {
    yesterdayCandidates.sort((a, b) => {
      const aDep = getEffectiveDepartureDate(a);
      const bDep = getEffectiveDepartureDate(b);
      return bDep.localeCompare(aDep);
    });
  }

  // 2. Identify today's candidates (reservations that cover todayStr)
  const todayCandidates = mergedResList.filter(r => {
    if (isCancelled(r)) return false;
    const arr = parseFdoDateToYYYYMMDD(r.arrivalDate);
    const dep = getEffectiveDepartureDate(r);
    return arr && dep && todayStr >= arr && todayStr <= dep;
  });

  let yesterdayRes = null;
  let todayRes = null;

  if (todayCandidates.length > 0) {
    // Sort today's candidates to prioritize staying/checking-in guests over checking-out guests.
    // Also prioritize active (not checked out/cancelled) reservations over checked-out/cancelled ones.
    todayCandidates.sort((a, b) => {
      const aCheckedOut = isCheckedOutOrCancelled(a);
      const bCheckedOut = isCheckedOutOrCancelled(b);

      if (aCheckedOut !== bCheckedOut) {
        return aCheckedOut ? 1 : -1; // active reservation comes first
      }

      const aDep = getEffectiveDepartureDate(a);
      const bDep = getEffectiveDepartureDate(b);

      const aIsCheckoutToday = (aDep === todayStr);
      const bIsCheckoutToday = (bDep === todayStr);

      if (aIsCheckoutToday && !bIsCheckoutToday) return 1; // b comes first
      if (!aIsCheckoutToday && bIsCheckoutToday) return -1; // a comes first

      // If both are checkout-today or both are staying, sort by arrival date (earlier first)
      return a.arrivalDate.localeCompare(b.arrivalDate);
    });

    todayRes = todayCandidates[0];
  }

  // 3. Assign yesterday's reservation (excluding today's selected reservation if possible to avoid duplicates)
  const yesterdayFiltered = yesterdayCandidates.filter(r => r.reservationId !== todayRes?.reservationId);
  if (yesterdayFiltered.length > 0) {
    yesterdayRes = yesterdayFiltered[0];
  } else if (yesterdayCandidates.length > 0) {
    yesterdayRes = yesterdayCandidates[0];
  }

  return { yesterdayRes, todayRes };
};

// Determine room status based on yesterday and today reservations
const determineRoomStatus = (yesterdayRes, todayRes, todayStr = getPakistanDateString()) => {
  const todayReservationId = todayRes ? todayRes.reservationId : null;
  const yesterdayReservationId = yesterdayRes ? yesterdayRes.reservationId : null;

  if (isUpcomingCheckout(todayRes, todayStr)) {
    return 'Upcoming Checkout';
  }

  const todayIsCheckedOut = isCheckedOutOrCancelled(todayRes);
  const yesterdayIsCheckedOut = isCheckedOutOrCancelled(yesterdayRes);

  // If all present reservations are checked out (or cancelled/available/null), then the room is vacant!
  const noActiveReservations = (!yesterdayRes || yesterdayIsCheckedOut) && (!todayRes || todayIsCheckedOut);
  if (noActiveReservations) {
    return 'Vacant';
  }

  // Check if today's or yesterday's reservation status is "Staying Guest"
  const isTodayStaying = todayRes && String(todayRes.apartmentStatus || '').trim().toLowerCase() === 'staying guest';
  const isYesterdayStaying = yesterdayRes && String(yesterdayRes.apartmentStatus || '').trim().toLowerCase() === 'staying guest';

  if ((isTodayStaying && !todayIsCheckedOut) || (isYesterdayStaying && !yesterdayIsCheckedOut)) {
    return 'Occupied';
  }

  const todayIsCheckin = todayRes && parseFdoDateToYYYYMMDD(todayRes.arrivalDate) === todayStr;
  const yesterdayIsCheckout = yesterdayRes && parseFdoDateToYYYYMMDD(yesterdayRes.departureDate) === todayStr;

  // If checked out but departure is today → still treat as Checkout (room needs cleaning today)
  const isCheckoutToday = yesterdayRes && parseFdoDateToYYYYMMDD(yesterdayRes.departureDate) === todayStr && yesterdayIsCheckedOut;
  const todayResIsCheckoutDay = todayRes && parseFdoDateToYYYYMMDD(todayRes.departureDate) === todayStr && todayIsCheckedOut;

  const hasToday = todayReservationId && (!todayIsCheckedOut || todayResIsCheckoutDay);
  const hasYesterday = yesterdayReservationId && (!yesterdayIsCheckedOut || isCheckoutToday);

  if (hasToday && hasYesterday) {
    if (todayReservationId === yesterdayReservationId) {
      // Same reservation — if it's a checkout-today, show Checkout
      if (isCheckoutToday || todayResIsCheckoutDay) return 'Checkout';
      return 'Occupied';
    } else if (todayIsCheckin && yesterdayIsCheckout && !todayIsCheckedOut) {
      return 'Occupied';
    } else if (isCheckoutToday || todayResIsCheckoutDay) {
      // One or both checked out today (departure = today) — room needs cleaning
      return 'Checkout';
    } else {
      return 'Occupied';
    }
  } else if (hasToday && !hasYesterday && todayIsCheckin && !todayIsCheckedOut) {
    return 'Checkin';
  } else if (hasToday && !hasYesterday && todayResIsCheckoutDay) {
    return 'Checkout';
  } else if (!hasToday && hasYesterday && yesterdayIsCheckout) {
    return 'Checkout';
  } else {
    return 'Vacant';
  }
};

// Custom styles for each room status
const getRoomStatusStyle = (status) => {
  const config = {
    'Vacant': { bg: "#f0fdf4", color: "#16a34a", border: "#86efac" }, // Green
    'Occupied': { bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" }, // Red
    'Checkin': { bg: "#eff6ff", color: "#2563eb", border: "#93c5fd" }, // Blue
    'Checkout': { bg: "#fffbeb", color: "#d97706", border: "#fde68a" }, // Orange/Amber
    'Upcoming Checkout': { bg: "#fff7ed", color: "#c2410c", border: "#fdba74" }, // Deep Orange
    'Checkin & Checkout': { bg: "#faf5ff", color: "#7c3aed", border: "#d8b4fe" }, // Purple
  }[status] || { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" };

  return config;
};

function Rooms() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = 2; // Rooms tab is index 2

  const [apartments, setApartments] = useState([]);
  const [cleaningList, setCleaningList] = useState([]);
  const [fdoList, setFdoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, color: "info", title: "", message: "" });
  const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));


  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      navigate("/cleaning");
    } else if (newValue === 1) {
      navigate("/cleaning-status");
    } else if (newValue === 2) {
      navigate("/rooms");
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Fetch Room Details (Base canonical apartments list)
        const roomDetailsResponse = await fetch(API_ENDPOINTS.ROOM_DETAILS_TEABLE_DATA, {
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token") || localStorage.getItem("authToken")}`
          }
        });

        if (!roomDetailsResponse.ok) {
          throw new Error(`Failed to fetch room details: ${roomDetailsResponse.status}`);
        }

        const roomDetailsResult = await roomDetailsResponse.json();

        let uniqueNames = [];
        if (roomDetailsResult.success && roomDetailsResult.data) {
          const names = roomDetailsResult.data
            .map(record => {
              const fields = record.fields || {};
              return fields["Apartment Name "] || fields["Apartment Name"] || "";
            })
            .filter(name => name.trim() !== "");

          uniqueNames = [...new Set(names)].sort((a, b) => a.localeCompare(b));
          setApartments(uniqueNames);
        } else {
          throw new Error(roomDetailsResult.message || "Failed to load room details data");
        }

        // 2. Fetch Cleaning data
        try {
          const cleaningResponse = await fetch("/api/cleaning", {
            headers: {
              "Authorization": `Bearer ${localStorage.getItem("token") || localStorage.getItem("authToken")}`
            }
          });

          if (cleaningResponse.ok) {
            const cleaningResult = await cleaningResponse.json();
            setCleaningList(cleaningResult.data || []);
          } else {
            console.warn("Cleaning fetch response not OK:", cleaningResponse.status);
          }
        } catch (cleanErr) {
          console.error("Failed to fetch cleaning records:", cleanErr);
        }

        // 3. Fetch FDO Main (Reservations) directly from PostgreSQL
        try {
          let allFdoRecords = [];
          const token = localStorage.getItem("token") || localStorage.getItem("authToken");
          const fdoResponse = await fetch("/api/fdo/reservations?source=rooms", {
            headers: {
              ...(token ? { "Authorization": `Bearer ${token}` } : {}),
              "Content-Type": "application/json"
            }
          });

          if (fdoResponse.ok) {
            const fdoResult = await fdoResponse.json();
            const rawRecords = fdoResult.records || [];
            allFdoRecords = rawRecords.map(r => ({
              id: String(r.reservation_id),
              fields: {
                "Reservation ID": String(r.reservation_id),
                "Reservation ID ": String(r.reservation_id),
                "Listing Name": r.listing_name,
                "Apartment Name": r.listing_name,
                "Apartment Name ": r.listing_name,
                "Arrival Date": r.arrival_date ? String(r.arrival_date).split('T')[0] : "",
                "Departure Date": r.departure_date ? String(r.departure_date).split('T')[0] : "",
                "Actual Checkin": r.actual_checkin ? formatFullDateTime(r.actual_checkin) : "",
                "Actual Checkout": r.actual_checkout ? formatFullDateTime(r.actual_checkout) : "",
                "Status": r.status || ""
              }
            }));
            console.log(`✅ Loaded ${allFdoRecords.length} live reservations from PostgreSQL for Rooms layout`);
          } else {
            console.warn("PostgreSQL reservations fetch response not OK:", fdoResponse.status);
          }

          // Filter double reservations on the same day for a room helper
          const filterDoubleReservations = (list) => {
            if (!list || list.length === 0) return [];
            const toRemove = new Set();

            const normRoom = (name) => {
              if (!name) return "";
              return String(name).replace(/\s+/g, "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            };

            const parseDate = (d) => {
              if (!d) return "";
              const clean = String(d).trim();
              if (clean.toLowerCase() === "n/a" || clean === "") return "";
              if (clean.includes("/")) {
                const parts = clean.split(",")[0].trim().split("/");
                if (parts.length === 3) {
                  const month = parts[0].padStart(2, "0");
                  const day = parts[1].padStart(2, "0");
                  const year = parts[2];
                  return `${year}-${month}-${day}`;
                }
              } else if (clean.includes("-")) {
                return clean.split("T")[0];
              }
              return clean;
            };

            for (let i = 0; i < list.length; i++) {
              const r1 = list[i];
              const f1 = r1.fields || {};
              const r1ResId = String(f1["Reservation ID"] || f1["Reservation ID "] || "").trim();
              const r1Room = normRoom(f1["Listing Name"] || f1["Apartment Name"] || f1["Apartment Name "] || "");
              const r1Arrival = parseDate(f1["Arrival Date"] || f1["Arrival Date "] || "");
              const r1Departure = parseDate(f1["Departure Date"] || f1["Departure Date "] || "");

              for (let j = i + 1; j < list.length; j++) {
                const r2 = list[j];
                const f2 = r2.fields || {};
                const r2ResId = String(f2["Reservation ID"] || f2["Reservation ID "] || "").trim();
                const r2Room = normRoom(f2["Listing Name"] || f2["Apartment Name"] || f2["Apartment Name "] || "");
                const r2Arrival = parseDate(f2["Arrival Date"] || f2["Arrival Date "] || "");
                const r2Departure = parseDate(f2["Departure Date"] || f2["Departure Date "] || "");

                const sameRoom = r1Room === r2Room && r1Room !== "" && r1Room !== "n/a";
                const sameArrival = r1Arrival === r2Arrival && r1Arrival !== "";
                const sameDeparture = r1Departure === r2Departure && r1Departure !== "";

                if (sameRoom && sameArrival && sameDeparture) {
                  const r1CheckinVal = f1["Actual Checkin"] || "";
                  const r1CheckoutVal = f1["Actual Checkout"] || "";
                  const r2CheckinVal = f2["Actual Checkin"] || "";
                  const r2CheckoutVal = f2["Actual Checkout"] || "";

                  const r1Checkin = r1CheckinVal && r1CheckinVal !== "N/A" && String(r1CheckinVal).trim() !== "";
                  const r1Checkout = r1CheckoutVal && r1CheckoutVal !== "N/A" && String(r1CheckoutVal).trim() !== "";
                  const r2Checkin = r2CheckinVal && r2CheckinVal !== "N/A" && String(r2CheckinVal).trim() !== "";
                  const r2Checkout = r2CheckoutVal && r2CheckoutVal !== "N/A" && String(r2CheckoutVal).trim() !== "";

                  if (r1Checkin && r1Checkout && r2Checkin && !r2Checkout) {
                    toRemove.add(r1ResId);
                  } else if (r2Checkin && r2Checkout && r1Checkin && !r1Checkout) {
                    toRemove.add(r2ResId);
                  }
                }
              }
            }
            return list.filter((r) => {
              const resId = String((r.fields || {})["Reservation ID"] || (r.fields || {})["Reservation ID "] || "").trim();
              return !toRemove.has(resId);
            });
          };

          const filteredFdoRecords = filterDoubleReservations(allFdoRecords);

          setFdoList(filteredFdoRecords);
          console.log(`✅ FDO fetch complete: ${filteredFdoRecords.length} filtered records (originally ${allFdoRecords.length})`);
        } catch (fdoErr) {
          console.error("Failed to fetch FDO Main table reservations:", fdoErr);
        }

      } catch (err) {
        console.error("Error fetching rooms data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const syncRoomsToTeable = async (silent = false) => {
    if (apartments.length === 0) return;
    if (!silent) {
      setSyncing(true);
    }

    try {
      const roomsToSync = apartments.map((name) => {
        const normName = String(name).trim().toLowerCase();

        const roomFdoMatches = fdoList.filter(r => {
          const f = r.fields || {};
          const listingName = f["Listing Name"] || f["Apartment Name"] || f["Apartment Name "] || "";
          return String(listingName).trim().toLowerCase() === normName;
        });

        const roomCleaningMatches = cleaningList.filter(item => {
          const base = getBaseName(item.name);
          return base.toLowerCase() === normName;
        });

        const mergedResList = mergeReservations(roomFdoMatches, roomCleaningMatches);
        const filteredMergedResList = filterDoubleReservations(mergedResList);
        const { yesterdayRes, todayRes } = classifyReservations(filteredMergedResList);

        const activeRes = todayRes || yesterdayRes;

        let currentCleaningStatus = "To do";
        if (activeRes && activeRes.column) {
          currentCleaningStatus = activeRes.column;
        } else if (roomCleaningMatches.length > 0) {
          currentCleaningStatus = roomCleaningMatches[0].column || "To do";
        }

        const todayStr = getPakistanDateString();
        const roomStatus = determineRoomStatus(yesterdayRes, todayRes, todayStr);

        return {
          apartmentName: name,
          apartmentStatus: roomStatus,
          cleaningStatus: currentCleaningStatus,
          checkInTime: activeRes ? activeRes.actualCheckin || "N/A" : "N/A",
          checkOutTime: activeRes ? activeRes.actualCheckout || "N/A" : "N/A",
          reservationId: activeRes && activeRes.reservationId && activeRes.reservationId !== "N/A" ? activeRes.reservationId : null
        };
      });

      const response = await fetch("/api/cleaning/sync-rooms", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || localStorage.getItem("authToken")}`
        },
        body: JSON.stringify({ rooms: roomsToSync })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || `Sync failed: ${response.status}`);
      }

      const result = await response.json();
      if (!silent) {
        setSnackbar({
          open: true,
          color: "success",
          title: "Sync Success",
          message: `Rooms synchronized! (Created: ${result.createdCount}, Updated: ${result.updatedCount})`
        });
      } else {
        console.log("Automatic room synchronization complete:", result);
      }
    } catch (err) {
      console.error("Room synchronization error:", err);
      if (!silent) {
        setSnackbar({
          open: true,
          color: "error",
          title: "Sync Error",
          message: err.message || "Failed to synchronize rooms to Teable"
        });
      }
    } finally {
      if (!silent) {
        setSyncing(false);
      }
    }
  };

  useEffect(() => {
    if (!loading && !error && apartments.length > 0) {
      syncRoomsToTeable(true);
    }
  }, [loading, error, apartments, fdoList, cleaningList]);


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1.5, sm: 3 }} px={{ xs: 1, sm: 1.5, md: 2 }}>

        {/* ── Tab Bar (Shared Navigation) ── */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 3, sm: 4 } }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            centered
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                mx: { xs: 0.5, sm: 0.5 },
                px: { xs: 1.5, sm: 2, md: 3 },
                py: 1,
                borderRadius: "12px",
                transition: "all 0.3s ease",
                color: "#4b5563",
                backgroundColor: "transparent",
                minWidth: { xs: "55px", sm: "120px" },
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "& .MuiTab-iconWrapper": {
                  marginBottom: 0,
                  marginRight: 0,
                },
              },
              "& .Mui-selected": {
                color: "#000 !important",
                backgroundColor: "transparent !important",
                border: "1.5px solid #000",
                boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Tab
              icon={<CleaningServicesIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "28px" }} />}
              label={<Box sx={{ display: { xs: "none", sm: "block" } }}>Cleaning</Box>}
            />
            <Tab
              icon={<FactCheckIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "28px" }} />}
              label={<Box sx={{ display: { xs: "none", sm: "block" } }}>Cleaning Status</Box>}
            />
            <Tab
              icon={<MeetingRoomIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "28px" }} />}
              label={<Box sx={{ display: { xs: "none", sm: "block" } }}>Rooms</Box>}
            />
          </Tabs>
        </Box>

        {/* ── Main Content Container ── */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress color="info" />
            <MDTypography variant="body2" sx={{ ml: 2, color: "#64748b" }}>Loading apartments & details...</MDTypography>
          </Box>
        ) : error ? (
          <Card sx={{ p: 4, borderRadius: "16px", textAlign: "center" }}>
            <Icon color="error" sx={{ fontSize: "40px !important", mb: 1 }}>error_outline</Icon>
            <MDTypography color="error" variant="h6" fontWeight="bold">Failed to load rooms</MDTypography>
            <MDTypography color="text" variant="body2" sx={{ mt: 1 }}>{error}</MDTypography>
          </Card>
        ) : apartments.length === 0 ? (
          <Card sx={{ p: 4, borderRadius: "16px", textAlign: "center", border: "2px dashed #e2e8f0" }}>
            <Icon sx={{ fontSize: "40px !important", color: "#94a3b8", mb: 1 }}>search_off</Icon>
            <MDTypography variant="subtitle1" fontWeight="bold" color="text">No Apartments Found</MDTypography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {(() => {
              // Pre-calculate reservation status for sorting
              const apartmentsWithResStatus = apartments.map((name) => {
                const normName = String(name).trim().toLowerCase();

                const roomFdoMatches = fdoList.filter(r => {
                  const f = r.fields || {};
                  const listingName = f["Listing Name"] || f["Apartment Name"] || f["Apartment Name "] || "";
                  return String(listingName).trim().toLowerCase() === normName;
                });

                const roomCleaningMatches = cleaningList.filter(item => {
                  const base = getBaseName(item.name);
                  return base.toLowerCase() === normName;
                });

                const mergedResList = mergeReservations(roomFdoMatches, roomCleaningMatches);
                const filteredMergedResList = filterDoubleReservations(mergedResList);
                const { yesterdayRes, todayRes } = classifyReservations(filteredMergedResList);
                const todayStr = getPakistanDateString();
                const roomStatus = determineRoomStatus(yesterdayRes, todayRes, todayStr);
                const hasNoRes = roomStatus === 'Vacant';
                const isCheckout = roomStatus === 'Checkout';

                return {
                  name,
                  hasNoRes,
                  isCheckout,
                  roomStatus,
                  roomFdoMatches,
                  roomCleaningMatches,
                  yesterdayRes,
                  todayRes
                };
              });

              // Sort order: Vacant first, Upcoming Checkout, Checkout, then active reservations
              const statusOrder = { 'Vacant': 0, 'Upcoming Checkout': 1, 'Checkout': 2, 'Checkin & Checkout': 3, 'Checkin': 4, 'Occupied': 5 };
              const sortedApartments = [...apartmentsWithResStatus].sort((a, b) => {
                const orderA = statusOrder[a.roomStatus] ?? 5;
                const orderB = statusOrder[b.roomStatus] ?? 5;
                if (orderA !== orderB) return orderA - orderB;
                return a.name.localeCompare(b.name);
              });

              const vacantApartments = sortedApartments.filter(apt => apt.hasNoRes);
              const checkoutApartments = sortedApartments.filter(apt => apt.isCheckout);
              const reservationApartments = sortedApartments.filter(apt => !apt.hasNoRes && !apt.isCheckout);

              const renderCard = (aptData, absoluteIndex) => {
                const { name, hasNoRes, isCheckout, yesterdayRes, todayRes, roomCleaningMatches } = aptData;
                const activeRes = todayRes || yesterdayRes;
                const todayStr = getPakistanDateString();
                const roomStatus = determineRoomStatus(yesterdayRes, todayRes, todayStr);

                let currentCleaningStatus = "To do";
                if (activeRes && activeRes.column) {
                  currentCleaningStatus = activeRes.column;
                } else if (roomCleaningMatches.length > 0) {
                  currentCleaningStatus = roomCleaningMatches[0].column || "To do";
                }
                // Only Vacant rooms use compact card (no reservation details)
                // Checkout rooms show reservation details so staff know who checked out
                const isCompactCard = hasNoRes;

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={name}>
                    <Card
                      sx={{
                        p: 1.5,
                        borderRadius: "16px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#ffffff",
                        display: "flex",
                        flexDirection: "column",
                        height: isCompactCard ? "auto" : "100%",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                          borderColor: "#000000",
                        },
                      }}
                    >
                      {/* Header: Room Title & Status Chip */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={isCompactCard ? 0 : 1}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "6px",
                              backgroundColor: "#f8fafc",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px solid #f1f5f9",
                              color: "#475569"
                            }}
                          >
                            <MeetingRoomIcon sx={{ fontSize: "14px" }} />
                          </Box>
                          <Box>
                            <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.75rem" }}>
                              {name}
                            </MDTypography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {(() => {
                            const styleConfig = getRoomStatusStyle(roomStatus);
                            return (
                              <Chip
                                label={roomStatus}
                                size="small"
                                sx={{
                                  backgroundColor: styleConfig.bg,
                                  color: styleConfig.color,
                                  border: `1.2px solid ${styleConfig.border}`,
                                  fontWeight: "bold",
                                  fontSize: "0.55rem",
                                  borderRadius: "5px",
                                  height: "18px",
                                  px: 0.5
                                }}
                              />
                            );
                          })()}
                          {/* Show cleaning badge for both Vacant and Checkout rooms */}
                          {isCompactCard && renderStatusBadgeMini(currentCleaningStatus)}
                        </Box>
                      </Box>

                      {/* Divider & Reservation Details (Only shown for non-compact cards) */}
                      {!isCompactCard && (
                        <>
                          <Box sx={{ borderTop: "1px dashed #e2e8f0", mb: 1 }} />
                          <Box display="flex" gap={1.5} sx={{ flexGrow: 1 }}>

                            {/* Yesterday Reservation — Upcoming Checkout shows only Cleaning status */}
                            <Box sx={{ flex: 1, minWidth: 0, borderRight: "1px solid #f1f5f9", pr: 1.5 }}>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#475569", display: "block", mb: 0.8, fontSize: "0.62rem", borderBottom: "1px solid #f1f5f9", pb: 0.2, lineHeight: 1.1 }}>
                                Yesterday Reservation
                              </MDTypography>
                              {yesterdayRes ? (
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Cleaning:</MDTypography>
                                    {renderStatusBadgeMini(yesterdayRes.column)}
                                  </Box>
                                  {roomStatus !== 'Upcoming Checkout' && (<>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Res ID:</MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        component="a"
                                        href={`https://dashboard.hostaway.com/reservations/${yesterdayRes.reservationId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          color: "#2563eb",
                                          fontSize: "0.55rem",
                                          textDecoration: "none",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          "&:hover": {
                                            textDecoration: "underline",
                                            color: "#1d4ed8"
                                          }
                                        }}
                                      >
                                        {yesterdayRes.reservationId}
                                      </MDTypography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={0.5}>
                                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem", flexShrink: 0 }}>Status:</MDTypography>
                                      <MDTypography variant="caption" fontWeight="bold" sx={{ color: getStatusColor(yesterdayRes.apartmentStatus), fontSize: "0.55rem", textAlign: "right", wordBreak: "break-word" }}>
                                        {yesterdayRes.apartmentStatus}
                                      </MDTypography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Check in:</MDTypography>
                                      <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem", textAlign: "right" }}>
                                        {formatTimeCompact(yesterdayRes.actualCheckin)}
                                      </MDTypography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Check out:</MDTypography>
                                      <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem", textAlign: "right" }}>
                                        {formatTimeCompact(yesterdayRes.actualCheckout)}
                                      </MDTypography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Arrival:</MDTypography>
                                      <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem" }}>
                                        {yesterdayRes.arrivalDate || "N/A"}
                                      </MDTypography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Departure:</MDTypography>
                                      <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem" }}>
                                        {yesterdayRes.departureDate || "N/A"}
                                      </MDTypography>
                                    </Box>
                                  </>)}
                                </Box>
                              ) : (
                                <MDTypography variant="caption" sx={{ color: "#94a3b8", fontStyle: "italic", fontSize: "0.55rem" }}>No reservation info</MDTypography>
                              )}
                            </Box>

                            {/* Today Reservation — full width for Upcoming Checkout */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#475569", display: "block", mb: 0.8, fontSize: "0.62rem", borderBottom: "1px solid #f1f5f9", pb: 0.2, lineHeight: 1.1 }}>
                                Today reservation
                              </MDTypography>
                              {todayRes ? (
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Cleaning:</MDTypography>
                                    {renderStatusBadgeMini(todayRes.column)}
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Res ID:</MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      component="a"
                                      href={`https://dashboard.hostaway.com/reservations/${todayRes.reservationId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        color: "#2563eb",
                                        fontSize: "0.55rem",
                                        textDecoration: "none",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        "&:hover": {
                                          textDecoration: "underline",
                                          color: "#1d4ed8"
                                        }
                                      }}
                                    >
                                      {todayRes.reservationId}
                                    </MDTypography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={0.5}>
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem", flexShrink: 0 }}>Status:</MDTypography>
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: getStatusColor(todayRes.apartmentStatus), fontSize: "0.55rem", textAlign: "right", wordBreak: "break-word" }}>
                                      {todayRes.apartmentStatus}
                                    </MDTypography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Check in:</MDTypography>
                                    <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem", textAlign: "right" }}>
                                      {formatTimeCompact(todayRes.actualCheckin)}
                                    </MDTypography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Check out:</MDTypography>
                                    <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem", textAlign: "right" }}>
                                      {formatTimeCompact(todayRes.actualCheckout)}
                                    </MDTypography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Arrival:</MDTypography>
                                    <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem" }}>
                                      {todayRes.arrivalDate || "N/A"}
                                    </MDTypography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold", fontSize: "0.55rem" }}>Departure:</MDTypography>
                                    <MDTypography variant="caption" sx={{ color: "#475569", fontSize: "0.55rem" }}>
                                      {todayRes.departureDate || "N/A"}
                                    </MDTypography>
                                  </Box>
                                </Box>
                              ) : (
                                <MDTypography variant="caption" sx={{ color: "#94a3b8", fontStyle: "italic", fontSize: "0.55rem" }}>No reservation info</MDTypography>
                              )}
                            </Box>
                          </Box>
                        </>
                      )}

                    </Card>
                  </Grid>
                );
              };

              return (
                <>
                  {/* 1. Vacant Apartments — top priority */}
                  {vacantApartments.map((aptData, idx) => renderCard(aptData, idx))}

                  {/* Row break: Checkout starts on a new line */}
                  {vacantApartments.length > 0 && checkoutApartments.length > 0 && (
                    <Grid item xs={12} sx={{ py: 0, my: 0 }} />
                  )}

                  {/* 2. Checkout Apartments — checked out today, needs cleaning */}
                  {checkoutApartments.map((aptData, idx) => renderCard(aptData, vacantApartments.length + idx))}

                  {/* Row break: Active reservations start on a new line */}
                  {(vacantApartments.length > 0 || checkoutApartments.length > 0) && reservationApartments.length > 0 && (
                    <Grid item xs={12} sx={{ py: 0, my: 0 }} />
                  )}

                  {/* 3. Active Reservations — Checkin & Checkout, Checkin, Occupied (combined) */}
                  {reservationApartments.map((aptData, idx) => renderCard(aptData, vacantApartments.length + checkoutApartments.length + idx))}
                </>
              );
            })()}
          </Grid>
        )}

      </MDBox>
      <MDSnackbar
        color={snackbar.color}
        icon="notifications"
        title={snackbar.title}
        content={snackbar.message}
        dateTime="Just now"
        open={snackbar.open}
        close={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Rooms;
